/**
 * Una tarjeta de consumo: icono, «usado de tope», dónut y aviso.
 *
 * La comparten las dos pantallas de uso —la del proyecto, con los topes de
 * cada sitio, y la de la cuenta, con la bolsa común—. Estaba escrita dos veces
 * dentro de la del proyecto, una por grupo, y con dos pantallas serían cuatro.
 */
import type { CSSProperties, ComponentType, ReactNode } from 'react'
import { IconAlertCircle, IconInfoCircle } from '@tabler/icons-react'
import type { Translate } from '@webbuilder/i18n'
import type { BillingPlan } from '../../../lib/api'
import { activeLanguage, useI18n } from '../../../lib/i18n'
import { formatPlanLimit, usageAlertMessage, usageRatio, usageState, type UsageResource } from '../../../lib/planLimits'

const GB = 1024 * 1024 * 1024
const MB = 1024 * 1024

/** Bytes → "2.4 GB" / "450 MB". Unlimited (null) → "∞". */
export function fmtBytes(n: number | null): string {
  if (n === null) return '∞'
  if (n >= GB) return `${(n / GB).toFixed(n % GB === 0 ? 0 : 1)} GB`
  return `${Math.max(0, Math.round(n / MB))} MB`
}

export function formatUsageReset(resetAt: string, t: Translate, nowMs = Date.now()): string {
  const reset = new Date(resetAt)
  if (!Number.isFinite(reset.getTime())) return ''
  const totalMinutes = Math.max(0, Math.ceil((reset.getTime() - nowMs) / 60_000))
  const days = Math.floor(totalMinutes / 1_440)
  if (days > 0) return t(days === 1 ? 'usage.resetsInDay' : 'usage.resetsInDays', { count: days })
  const hours = Math.floor(totalMinutes / 60)
  if (hours > 0) return t(hours === 1 ? 'usage.resetsInHour' : 'usage.resetsInHours', { count: hours })
  return totalMinutes > 0 ? t('usage.resetsInMinutes', { count: totalMinutes }) : t('usage.resetsSoon')
}

export type UsageStat = {
  id: UsageResource
  label: string
  description: string
  icon: ComponentType<{ size?: number }>
  used: number
  /** `null` = sin tope: sin barra ni porcentaje. */
  limit: number | null
  /** Lo que se lee en vez del número crudo («2 GB», «40%»). Por defecto, el número. */
  usedLabel?: string
  limitLabel?: string
  /** «Se reinicia en 3 días», debajo de la cifra. */
  resetDescription?: string
}

export function UsageStatCard({ stat, plan, onOpenPlans, action }: { stat: UsageStat; plan: BillingPlan; onOpenPlans: () => void; action?: ReactNode }) {
  const { t } = useI18n()
  const unlimited = stat.limit === null
  const percentage = unlimited ? 0 : Math.min(100, Math.round((stat.used / (stat.limit || 1)) * 100))
  const state = usageState(stat.used, stat.limit)
  const alertMessage = usageAlertMessage(stat.id, state, plan, usageRatio(stat.used, stat.limit), t)
  const Icon = stat.icon
  return (
    <div className="dash-usage-card">
      <div className="dash-site-usage-card-head">
        <span className="dash-widget-glass-icon dash-usage-card-icon" aria-hidden="true">
          <Icon size={18} />
        </span>
        <div className="dash-site-usage-card-copy">
          <div className="dash-site-usage-title-line">
            <h3 className="dash-card-title">{stat.label}</h3>
            {/*
              Uno de los dos, nunca los dos.

              Eran una pastilla roja y una «i» pegadas al rótulo, y con
              el contador lleno salían juntas: dos círculos seguidos que
              compiten por el mismo clic para contar cosas que van de lo
              mismo. Cuando hay aviso, la explicación del contador viaja
              DENTRO del aviso —que además ya sabe repartir una frase en
              varias líneas— y la «i» se retira. Sin aviso, la «i» vuelve.
            */}
            {alertMessage ? (
              <span className={`dash-usage-alert is-${state}`} role="img" aria-label={`${alertMessage} ${stat.description}`} data-tooltip={`${alertMessage} ${stat.description}`}>
                <IconAlertCircle size={13} aria-hidden="true" />
              </span>
            ) : (
              <button className="dash-site-usage-info" type="button" aria-label={t('usage.about', { label: stat.label })}>
                <IconInfoCircle size={13} />
                <span role="tooltip">{stat.description}</span>
              </button>
            )}
          </div>
          <div className="dash-site-usage-values">
            <span className="dash-card-metric">{stat.usedLabel ?? stat.used.toLocaleString(activeLanguage())}</span>
            <span className="dash-card-meta">{t('usage.of', { limit: stat.limitLabel ?? formatPlanLimit(stat.limit) })}</span>
          </div>
          {stat.resetDescription ? <p className="dash-card-caption dash-site-usage-reset">{stat.resetDescription}</p> : null}
        </div>
        <span
          className={`dash-site-usage-percent is-${state}`}
          style={{ '--dash-usage-progress': `${percentage * 3.6}deg` } as CSSProperties}
          aria-label={unlimited ? t('usage.unlimited') : t('usage.percentUsed', { percent: percentage })}
        >
          {unlimited ? '∞' : `${percentage}%`}
        </span>
      </div>
      {action ? <div className="dash-site-usage-card-action">{action}</div> : null}
      {state !== 'ok' ? (
        <div className="dash-site-usage-card-action">
          <button className="dash-card-caption dash-text-link" style={{ background: 'transparent', border: 'none', color: 'var(--dash-text)', cursor: 'pointer', padding: 0 }} onClick={onOpenPlans}>
            {t('usage.raiseLimit')}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/** El hueco de una tarjeta mientras llega su cifra. */
export function UsageStatSkeleton() {
  return (
    <div className="dash-usage-card" aria-hidden="true">
      <div className="dash-site-usage-card-head">
        <span className="sk" style={{ width: 36, height: 36, borderRadius: 11 }} />
        <div className="dash-site-usage-card-copy">
          <span className="sk" style={{ width: 130, height: 14, borderRadius: 6 }} />
          <span className="sk" style={{ width: 72, height: 12, borderRadius: 6 }} />
        </div>
        <span className="sk dash-site-usage-skeleton-donut" />
      </div>
    </div>
  )
}
